import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { manageTableCSVReportApi, downloadCSVApi } from 'api/analytics/review';
import { useQuery as useQueryParams } from 'components/hooks/useQuery';
import { useToast } from 'components/hooks/useToast';
import { useHistory } from 'react-router-dom';
import { useReviewManageSidepanelData } from 'reducers/reviewManageSidepanelSlice';
import { useSelectedView } from 'reducers/viewsSlice';
import { TABLE_TYPES_EVENT_VALUE_NAME } from 'screens/performanceReview/LandingPage/ReviewAuditIntent';
import { getReviewsDetailedReportsApi } from 'screens/performanceReview/manageAndInsights/ReviewDetailedReport/dataHooks';
import { reviewStatusValueGetterCSV } from 'screens/performanceReview/manageAndInsights/ReviewManage/components/ReviewManageAGGrid/components/ReviewStatusV1';
import { employeesValueGetterCSV } from 'screens/performanceReview/manageAndInsights/ReviewManage/components/ReviewManageAGGrid/components/cellComponents/EmpGroupAvatars';

import { Download, Refresh2 } from 'assets/assetsV1/iconComponents';

import {
  CSV_DOWNLOAD_API_POLL_INTERVAL,
  CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL,
  TOAST_STATUSES,
} from 'constants/ProjectConstants';
import { localStorageKeys } from 'constants/localStorageKeys';
import { sendDownloadReviewCSVEvent } from 'constants/mixpanelHelpers/reviewsEvents';
import { REVIEW_INTENTS } from 'constants/moduleIntentTypes';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { getStatusOfRequestIds } from 'services/analytics/review';

import { useSelectReviewsSelectedIntent } from '../slice/ReviewsIntentSlice';

function useReviewManageApiDownloadHandler({ downloadApiHandler, localStorageKey, payload }) {
  const [isFetching, setIsFetching] = useState(false);
  const toast = useToast();

  const wait = (ms = 1000) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const downloadManageCSVApi = () => downloadApiHandler(payload);

  const onDownloadToSystemComplete = () => {
    try {
      window.localStorage.removeItem(localStorageKey);
      setIsFetching(false);
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  const onError = (error) => {
    setIsFetching(false);
    console.error('Error in CSV download : ', error);
  };

  const downloadToSystem = async (blobData) => {
    try {
      if (blobData) {
        const { fetchedBlob: blob, name } = blobData;
        // 2. Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', name);
        // 3. Append to html page
        document.body.appendChild(link);
        // 4. Force download
        link.click();
        // 5. Clean up and remove the link
        link.parentNode.removeChild(link);
        onDownloadToSystemComplete();
      }
    } catch (e) {
      onError(e);
    }
  };

  const showDownloadSuccessToast = () => {
    toast({
      title: 'Download complete!',
      status: 'success',
    });
    // remove from localstorage after toast duration i.e. 5 sec, because user will not have a way to download it anyway
    setTimeout(() => {
      setIsFetching(false);
      window.localStorage.removeItem(localStorageKey);
    }, 5000);
  };

  const showDownloadStartInfoToast = () => {
    toast({
      title: 'Generating report',
      status: 'info',
      duration: 5000,
    });
  };

  const showDownloadAlreadyInProgressToast = () => {
    toast({
      title:
        'Report generation is already in progress! Please wait for that to complete or refresh the page to start again',
      status: 'info',
    });
  };

  const setGeneratedFilesToLS = (res) => {
    try {
      const prevGeneratedFiles = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];

      const generatedFiles = [...prevGeneratedFiles, { ...res, requestedTS: Date.now() }];

      const stringifiedGeneratedFiles = JSON.stringify(generatedFiles);

      window.localStorage.setItem(localStorageKey, stringifiedGeneratedFiles);
    } catch (e) {
      console.error('Error in LS : ', e);
    }
  };

  const onSuccess = (blobData) => {
    setIsFetching(false);
    showDownloadSuccessToast();
    downloadToSystem(blobData);
  };

  const downloadCSVBlob = async (requestId, name) => {
    try {
      const response = await downloadCSVApi(requestId);
      const fetchedBlob = await response.blob();
      onSuccess({
        fetchedBlob,
        requestId,
        name,
      });
    } catch (err) {
      console.error('Error in downloading CSV ', err);
    }
  };

  const apiPoll = async (getStatusForRequestIds, validateStatusResponse, pollingInterval) => {
    try {
      let result = await getStatusForRequestIds();
      while (validateStatusResponse(result)) {
        // eslint-disable-next-line no-await-in-loop
        await wait(pollingInterval);
        // eslint-disable-next-line no-await-in-loop
        result = await getStatusForRequestIds();
      }

      if (Array.isArray(result)) {
        result.forEach((result1) => {
          downloadCSVBlob(result1.uuid, result1.name);
        });
      }
    } catch (err) {
      console.error('Error in fetching status of CSV ', err);
    }
  };

  const validateStatusResponse = (res = []) =>
    res
      .map((report) => {
        switch (report.status) {
          case 'IN_PROGRESS':
            return true;
          case 'FINISHED':
            return false;
          case 'ERROR': {
            onError('Error in generating file');
            return false;
          }
          default:
            onError('Error in generating file');

            return false;
        }
      })
      .reduce((prev, curr) => prev || curr, false);

  const getStatusForRequestIds = async () => {
    try {
      const downloadRequests = JSON.parse(window.localStorage.getItem(localStorageKey)) ?? [];

      if (downloadRequests.length) {
        const currentTS = Date.now();

        // files which were requested CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL back are deleted from LS
        const validDownloadRequests = downloadRequests.filter(
          (request) => currentTS - request.requestedTS <= CSV_DOWNLOAD_FILE_REQUEST_WAIT_INTERVAL
        );

        window.localStorage.setItem(localStorageKey, JSON.stringify(validDownloadRequests ?? []));

        validDownloadRequests.forEach(() => {
          setIsFetching(true);
        });

        const response = await getStatusOfRequestIds(validDownloadRequests);
        return response;
      }
    } catch (e) {
      console.error('Error in LS : ', e);
    }
    return [];
  };

  const generateFile = async () => {
    if (isFetching) {
      showDownloadAlreadyInProgressToast();
      return;
    }

    showDownloadStartInfoToast();
    try {
      setIsFetching(true);

      // makes the initial download request and returns request metadata to be used for polling
      const response = await downloadManageCSVApi();
      setGeneratedFilesToLS(response.entity);
      apiPoll(getStatusForRequestIds, validateStatusResponse, CSV_DOWNLOAD_API_POLL_INTERVAL);
    } catch (e) {
      window.localStorage.removeItem(localStorageKey);
    }
  };

  useEffect(() => {
    setIsFetching(false);
    window.localStorage.removeItem(localStorageKey);
  }, [localStorageKey]);

  return {
    handleDetailedDownload: generateFile,
  };
}

const useReviewsManageIntentEllipsisValues = ({ gridRef }) => {
  const cohortDetails = useSelectedView();
  const { selectedReviewCycle } = useReviewManageSidepanelData();

  const queryClient = useQueryClient();
  const toast = useToast();

  // const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshAgGridData = useCallback(async () => {
    // if (isRefreshing) {
    //   return;
    // }
    try {
      // setIsRefreshing(true);
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewsManage]);
    } catch (error) {
      toast({
        title: 'Error reloading. Please try again.',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      console.error('Error reloading reviews table data');
    } finally {
      // setIsRefreshing(false);
    }
  }, [
    // isRefreshing,
    queryClient,
    toast,
  ]);

  const currentTableCsvExport = useCallback(() => {
    gridRef.current.api.exportDataAsCsv({
      fileName: 'review_explore_table.csv',
      allColumns: true,
      processCellCallback(params) {
        const {
          value,
          column: { colId },
        } = params;
        if (colId === '2') {
          return value.value.performanceGridName;
        }
        if (['40', '43', '44'].includes(colId)) {
          return reviewStatusValueGetterCSV(value);
        }
        if (['41', '42'].includes(colId)) {
          return employeesValueGetterCSV(value);
        }
        return value;
      },
    });
  }, [gridRef]);

  const downloadCSVApiData = (selectedCycle) => {
    const selectedSource = { ...selectedCycle };
    delete selectedSource.feedbackForms;
    return {
      cohort: cohortDetails,
      groupByKeys: {
        uuid: null,
        keyName: 'employeeId',
        displayName: 'Individual',
        enabled: true,
      },
      source: selectedSource,
      filter: null,
      orderBy: [],
      groupFilter: [],
      attributeFilter: null,
      filterApi: false,
      offset: 0,
      limit: 10000,
    };
  };

  const downloadApiHandler = (payload) => manageTableCSVReportApi({ type: 'reviewee', requestBody: payload });
  const localStorageKey = localStorageKeys.reviewTablesManageCSVDownloads;

  const { handleDetailedDownload } = useReviewManageApiDownloadHandler({
    downloadApiHandler,
    localStorageKey,
    payload: downloadCSVApiData(selectedReviewCycle),
  });

  const ellipsisOptions = useMemo(() => {
    const options = [
      {
        label: 'Refresh list',
        onClick: refreshAgGridData,
        Icon: Refresh2,
      },
      {
        label: 'Download report',
        Icon: Download,
        type: 'subMenu',
        subOptions: [
          {
            label: 'Current View Download',
            onClick: () => currentTableCsvExport(),
            description: `Download of review status and metrics across employees`,
          },
          {
            label: 'Detailed Download',
            onClick: () => handleDetailedDownload(),
            description: `Download of metrics and status for all review forms across employees`,
          },
        ],
      },
    ];

    return options;
  }, [currentTableCsvExport, handleDetailedDownload, refreshAgGridData]);

  return ellipsisOptions;
};

/**
 * Escapes a value for safe inclusion in a CSV:
 * - Wraps strings in double quotes to handle commas, line breaks, or special characters.
 * - Escapes double quotes by replacing them with double-double quotes (`""`).
 * - Returns non-string values (e.g., numbers) as-is.
 */
const escapeCsvValue = (value) => (typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value);

const convertToCSV = (columnDefs, allRowData) => {
  if (!columnDefs.length) return '';

  // Extract headers dynamically from AG Grid API
  const headers = `${columnDefs.map((col) => col.displayName).join(',')}\n`;

  // Convert custom data to CSV format based on AG Grid column fields
  const rows = allRowData
    .map((row) =>
      columnDefs
        .map((col) => {
          const value = row[col.colId];
          switch (col.columnType) {
            case 'EMPLOYEE':
              return escapeCsvValue(value?.displayName || '');
            case 'DATE':
              return value?.date || '';
            case 'DECIMAL':
              return typeof value === 'number' ? value.toFixed(2) : '';
            default:
              return escapeCsvValue(value || 'N.E.D');
          }
        })
        .join(',')
    )
    .join('\n');

  return headers + rows;
};

// Download CSV file
export const downloadCSVWithColsAndRows = (columnDefs, allRowData, name) => {
  const csvData = convertToCSV(columnDefs, allRowData);
  const blob = new Blob([csvData], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  window.URL.revokeObjectURL(url);
};

const useReviewsDetailsReportIntentEllipsisValues = ({ gridRef }) => {
  const cohortDetails = useSelectedView();
  const { reviewCycleId } = useQueryParams();

  const queryClient = useQueryClient();
  const toast = useToast();

  const refreshAgGridData = useCallback(async () => {
    try {
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewGenerateReport]);
    } catch (error) {
      toast({
        title: 'Error reloading. Please try again.',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      console.error('Error reloading reviews table data');
    }
  }, [queryClient, toast]);

  const currentTableCsvExport = useCallback(() => {
    gridRef.current.api.exportDataAsCsv({
      fileName: 'review_current_detailed_report.csv',
      allColumns: true,
      processCellCallback(params) {
        const {
          value,
          column: { colId },
        } = params;

        return value;
      },
    });
  }, [gridRef]);

  const downloadApiHandler = (payload) => getReviewsDetailedReportsApi(payload);

  const handleDetailedDownload = useCallback(async () => {
    if (!gridRef?.current?.api) return;

    const columnDefs = gridRef.current.api.getColumnDefs();

    const res = await downloadApiHandler({
      cohortId: cohortDetails?.id,
      reviewCycleId: reviewCycleId ? Number(reviewCycleId) : null,
      limit: null,
    });
    const allRowsData = res?.entity?.rows ?? [];
    // Generate CSV
    downloadCSVWithColsAndRows(columnDefs, allRowsData, 'review_all_rows_detailed_report.csv');
  }, [cohortDetails?.id, gridRef, reviewCycleId]);

  const ellipsisOptions = useMemo(() => {
    const options = [
      {
        label: 'Refresh list',
        onClick: refreshAgGridData,
        Icon: Refresh2,
      },
      {
        label: 'Download report',
        Icon: Download,
        type: 'subMenu',
        subOptions: [
          {
            label: 'Current View Download',
            onClick: () => currentTableCsvExport(),
            description: `Download review reports for employees with the current filters applied.`,
          },
          {
            label: 'Detailed Download',
            onClick: () => handleDetailedDownload(),
            description: `Download review reports for all employees with no filters applied.`,
          },
        ],
      },
    ];

    return options;
  }, [currentTableCsvExport, handleDetailedDownload, refreshAgGridData]);

  return ellipsisOptions;
};

const useCalibrationViewsIntentEllipsisValues = ({ gridRef }) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const refreshAgGridData = useCallback(async () => {
    try {
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.talentPlanning.tabularData]);
    } catch (error) {
      toast({
        title: 'Error reloading. Please try again.',
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
      console.error('Error reloading reviews table data');
    }
  }, [queryClient, toast]);

  const downLoadTableData = useCallback(async () => {
    gridRef.current?.api.exportDataAsCsv({
      fileName: 'talent_planning_table.csv',
      allColumns: true,
    });
  }, [gridRef]);

  const ellipsisOptions = useMemo(() => {
    const options = [
      {
        label: 'Refresh list',
        onClick: refreshAgGridData,
        Icon: Refresh2,
      },
      {
        label: 'Download report',
        Icon: Download,
        onClick: downLoadTableData,
      },
    ];

    return options;
  }, [downLoadTableData, refreshAgGridData]);

  return ellipsisOptions;
};

const useReviewsLandingIntentEllipsisValues = ({ gridRef }) => {
  const { queryParamValue } = useSelectReviewsSelectedIntent() ?? {};

  const downLoadTableData = useCallback(async () => {
    sendDownloadReviewCSVEvent({
      eventProperties: {
        type: 'Explore',
        activeTab: TABLE_TYPES_EVENT_VALUE_NAME[queryParamValue],
        source: 'reviews',
      },
    });
    gridRef.current?.api.exportDataAsCsv();
  }, [gridRef, queryParamValue]);

  const ellipsisOptions = useMemo(() => {
    const options = [
      {
        label: 'Download report',
        Icon: Download,
        onClick: downLoadTableData,
      },
    ];

    return options;
  }, [downLoadTableData]);

  return ellipsisOptions;
};

const useReviewsIntentEllipsisValues = ({ gridRef }) => {
  const history = useHistory();

  const isTalentPlanningPage = history.location.pathname.includes(routes.performanceReview.talentPlanning);

  const { id } = useSelectReviewsSelectedIntent() ?? {};

  const reviewManageOptions = useReviewsManageIntentEllipsisValues({ gridRef });
  const calibrationViewsOptions = useCalibrationViewsIntentEllipsisValues({ gridRef });
  const reviewLandingOptions = useReviewsLandingIntentEllipsisValues({ gridRef });
  const reviewDetailedReportOptions = useReviewsDetailsReportIntentEllipsisValues({ gridRef });
  switch (id) {
    case REVIEW_INTENTS.REVIEW_CALIBRATED_RESULTS:
      if (isTalentPlanningPage) return calibrationViewsOptions;
      return reviewManageOptions;

    case REVIEW_INTENTS.REVIEW_COMPLETION_STATUS:
      return reviewManageOptions;

    case REVIEW_INTENTS.REVIEW_DETAILED_REPORTS:
      return reviewDetailedReportOptions;

    default:
      return reviewLandingOptions;
  }
};

export { useReviewsIntentEllipsisValues, useReviewManageApiDownloadHandler };
